import {
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Select,
  Spinner,
  Textarea,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Add16Filled,
  Add16Regular,
  ArrowDownload16Filled,
  ArrowDownload16Regular,
  ArrowUpload16Filled,
  ArrowUpload16Regular,
  ChevronDown16Regular,
  ChevronRight16Regular,
  Copy16Filled,
  Copy16Regular,
  Delete16Filled,
  Delete16Regular,
  Dismiss16Regular,
  Edit16Filled,
  Edit16Regular,
  Filter16Filled,
  Filter16Regular,
  FullScreenMaximize16Filled,
  FullScreenMaximize16Regular,
  MoreHorizontal16Filled,
  MoreHorizontal16Regular,
  NotepadEdit16Filled,
  NotepadEdit16Regular,
  People16Filled,
  People16Regular,
  Prohibited16Filled,
  Prohibited16Regular,
} from '@fluentui/react-icons';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AppError } from '../../../api/errors.js';
import {
  COPY_TIMEOUT_MS,
  DATASET_ROLE_CONTRIBUTOR,
  DATASET_ROLE_ANNOTATOR,
  DATASET_ROLE_OWNER,
  DATASET_ROLE_TOOLTIP_CONTRIBUTOR,
  DATASET_ROLE_TOOLTIP_OWNER,
  EDIT_DESCRIPTION_ROWS,
  MEETING_DATA_ENTRY_TYPE,
  MEETING_DATA_TYPE_LABEL,
  MEETING_ENTRY_FIELDS,
  PARTICIPANT_FILTER_HIDE_LABEL,
  PARTICIPANT_FILTER_SHOW_LABEL,
  PARTICIPANT_FILTER_TOGGLE_ARIA,
  PARTICIPANT_SEARCH_EMPTY_MESSAGE,
  TAG_SEARCH_MATCH_MODE_AND,
  TAG_SEARCH_MATCH_MODE_OR,
} from '../../../constants/index.js';
import type { MeetingEntryFieldKey } from '../../../constants/index.js';
import { useDataHubClient } from '../../../context/DataHubClientContext.js';
import { useAttributeDefinitions } from '../../../hooks/useAttributeDefinitions/useAttributeDefinitions.js';
import { useDataEntryDetail } from '../../../hooks/useDataEntryDetail/useDataEntryDetail.js';
import { useDatasetEntries } from '../../../hooks/useDatasetEntries/useDatasetEntries.js';
import { useInlineEdit } from '../../../hooks/useInlineEdit/useInlineEdit.js';
import { hasDatasetRole } from '../../../hooks/useMyDatasetRole/hasDatasetRole.js';
import type {
  AttributeDefinition,
  DataEntry,
  DataEntryFileType,
  DataEntryTagFilterInput,
  DataFile,
  DatasetStatus,
} from '../../../services/dataHubService.interface.js';
import { DataHubService } from '../../../services/dataHubService.js';
import { withCorrelationId } from '../../../services/usageContext.js';
import { useScenarioFactory } from '../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../telemetry/scenarios.js';
import { RawJsonViewer } from '../../debug/RawJsonViewer/RawJsonViewer.js';
import { AddDataEntryDialog } from '../AddDataEntryDialog/AddDataEntryDialog.js';
import { DataFilePreview } from '../DatasetAnnotationsTab/AnnotationEditor/components/DataFilePreview/DataFilePreview.js';
import { canLoadDataFilePreview } from '../DatasetAnnotationsTab/AnnotationEditor/index.js';
import { DownloadDatasetDialog } from '../DownloadDatasetDialog/DownloadDatasetDialog.js';
import { ParticipantSearchPanel } from '../ParticipantSearchPanel/ParticipantSearchPanel.js';
import { UploadFolderDialog } from '../UploadFolderDialog/UploadFolderDialog.js';

import type {
  DatasetEntriesTableProps,
  TagFilter,
} from './DatasetEntriesTable.interface.js';
import { useStyles } from './DatasetEntriesTable.style.js';

const AddIcon = bundleIcon(Add16Filled, Add16Regular) as React.FC;
const UploadIcon = bundleIcon(
  ArrowUpload16Filled,
  ArrowUpload16Regular,
) as React.FC;
const CopyIcon = bundleIcon(Copy16Filled, Copy16Regular) as React.FC;
const DeleteIcon = bundleIcon(Delete16Filled, Delete16Regular) as React.FC;
const DownloadIcon = bundleIcon(
  ArrowDownload16Filled,
  ArrowDownload16Regular,
) as React.FC;
const EditIcon = bundleIcon(Edit16Filled, Edit16Regular) as React.FC;
const AnnotateIcon = bundleIcon(
  NotepadEdit16Filled,
  NotepadEdit16Regular,
) as React.FC;
const FilterIcon = bundleIcon(Filter16Filled, Filter16Regular) as React.FC;
const MaximizeIcon = bundleIcon(
  FullScreenMaximize16Filled,
  FullScreenMaximize16Regular,
) as React.FC;
const HardDeleteIcon = bundleIcon(
  Prohibited16Filled,
  Prohibited16Regular,
) as React.FC;
const MoreIcon = bundleIcon(
  MoreHorizontal16Filled,
  MoreHorizontal16Regular,
) as React.FC;
const PeopleIcon = bundleIcon(People16Filled, People16Regular) as React.FC;

const MATCH_MODE_AND = 'AND';
const MATCH_MODE_OR = 'OR';
const FILE_TYPE_OPTIONS = [
  { value: 'generic', label: 'Generic' },
  { value: 'videoStream', label: 'Video Stream' },
  { value: 'audioStream', label: 'Audio Stream' },
  { value: 'transcript', label: 'Transcript' },
] as const;
const FILE_TYPE_PLACEHOLDER = '';

type MatchMode = typeof MATCH_MODE_AND | typeof MATCH_MODE_OR;

function truncateId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}-...` : id;
}

// ── Entry expansion panel ─────────────────────────────────────────────────────

interface EntryExpansionPanelProps {
  entry: DataEntry;
  dataSetId: string;
  dataSetVersion: number;
  service: DataHubService;
  canContribute: boolean;
  canAnnotate: boolean;
  onEntryUpdated: () => void;
  onStatusChanged?: (status: DatasetStatus) => void;
  onAnnotateEntry?: (
    dataId: string,
    dataSetVersion: number,
    fileId?: string,
  ) => void;
  onOpenViewer?: (dataId: string, fileId?: string) => void;
}

function EntryExpansionPanel({
  entry,
  dataSetId,
  dataSetVersion,
  service,
  canContribute,
  canAnnotate,
  onEntryUpdated,
  onStatusChanged,
  onAnnotateEntry,
  onOpenViewer,
}: EntryExpansionPanelProps) {
  const styles = useStyles();
  const scenarioFactory = useScenarioFactory();
  const {
    entry: detail,
    isLoading,
    error,
    refetch: refetchDetail,
  } = useDataEntryDetail({
    dataSetId,
    dataId: entry.dataId,
    dataSetVersion,
    enabled: true,
  });

  const [localFiles, setLocalFiles] = useState<DataFile[] | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null,
  );
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [uploadFileType, setUploadFileType] = useState<string>(
    FILE_TYPE_PLACEHOLDER,
  );

  const displayEntry = detail ?? entry;

  const {
    editing,
    editName,
    editDesc,
    isSaving,
    editError,
    canSave,
    setEditName,
    setEditDesc,
    startEditing,
    cancelEdit,
    save,
  } = useInlineEdit(displayEntry.name ?? '', displayEntry.description ?? '');

  const isMeetingEntry = displayEntry.dataType === MEETING_DATA_ENTRY_TYPE;

  const entryMeetingValues = useMemo<Record<MeetingEntryFieldKey, string>>(
    () => ({
      meetingTitle: displayEntry.meetingTitle ?? '',
      language: displayEntry.language ?? '',
      meetingDate: displayEntry.meetingDate ?? '',
      organizerId: displayEntry.organizerId ?? '',
    }),
    [
      displayEntry.meetingTitle,
      displayEntry.language,
      displayEntry.meetingDate,
      displayEntry.organizerId,
    ],
  );

  const [editMeetingFields, setEditMeetingFields] =
    useState<Record<MeetingEntryFieldKey, string>>(entryMeetingValues);

  useEffect(() => {
    setEditMeetingFields(entryMeetingValues);
  }, [entryMeetingValues]);

  const setEditMeetingField = useCallback(
    (key: MeetingEntryFieldKey, value: string) => {
      setEditMeetingFields((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleCancelEdit = useCallback(() => {
    setEditMeetingFields(entryMeetingValues);
    cancelEdit();
  }, [cancelEdit, entryMeetingValues]);

  useEffect(() => {
    if (detail?.dataFiles != null) {
      setLocalFiles(detail.dataFiles);
    }
  }, [detail?.dataFiles]);

  const handleDownload = useCallback(
    async (file: DataFile) => {
      setFileError(null);
      setDownloadingFileId(file.id);
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeDownloadFile,
      );
      try {
        const downloadUrl = await service.getDataEntryFileDownloadUrl(
          dataSetId,
          entry.dataId,
          file.id,
          dataSetVersion,
          // Single-file download is a discrete user action → one "Download
          // Dataset" usage event per click, correlated to the source entry.
          {
            sourceId: entry.dataId,
            extras: withCorrelationId(scenario.queryId),
          },
        );
        scenario.complete('success');
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = file.fileName ?? file.id;
        anchor.click();
      } catch (err) {
        const classification = classifyScenarioError(err);
        scenario.complete(classification.status, classification.data);
        setFileError(
          err instanceof Error ? err.message : 'Failed to download file',
        );
      } finally {
        setDownloadingFileId(null);
      }
    },
    [service, dataSetId, entry.dataId, dataSetVersion, scenarioFactory],
  );

  const loadFileSource = useCallback(
    async (file: DataFile, signal: AbortSignal) => {
      const downloadUrl = await service.getDataEntryFileDownloadUrl(
        dataSetId,
        entry.dataId,
        file.id,
        dataSetVersion,
      );
      return service.readTextFileFromBlob(downloadUrl, signal);
    },
    [dataSetId, dataSetVersion, entry.dataId, service],
  );

  const handleDeleteFile = useCallback(
    async (fileId: string) => {
      setFileError(null);
      setDeletingFileId(fileId);
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeDeleteDataEntryFile,
      );
      try {
        await service.deleteDataEntryFile(dataSetId, entry.dataId, fileId, {
          extras: withCorrelationId(scenario.queryId),
        });
        scenario.complete('success');
        setLocalFiles((prev) =>
          prev !== null ? prev.filter((f) => f.id !== fileId) : prev,
        );
        setPreviewFileId((current) => (current === fileId ? null : current));
      } catch (err) {
        const classification = classifyScenarioError(err);
        scenario.complete(classification.status, classification.data);
        setFileError(
          err instanceof Error ? err.message : 'Failed to delete file',
        );
      } finally {
        setDeletingFileId(null);
      }
    },
    [service, dataSetId, entry.dataId, scenarioFactory],
  );

  const handleUploadSelect = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      setPendingUploadFile(file);
      setUploadFileType(FILE_TYPE_PLACEHOLDER);
    };
    input.click();
  }, []);

  const handleUploadConfirm = useCallback(async () => {
    if (pendingUploadFile === null || uploadFileType === FILE_TYPE_PLACEHOLDER)
      return;
    setIsUploading(true);
    setFileError(null);
    setPendingUploadFile(null);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeUploadFile,
    );
    try {
      const usage = { extras: withCorrelationId(scenario.queryId) };
      const { fileId, uploadUrl } = await service.generateFileUploadUrl(
        dataSetId,
        entry.dataId,
        pendingUploadFile.name,
        pendingUploadFile.type || undefined,
        usage,
      );
      scenario.mark('url_generated');
      await service.uploadFileToBlob(uploadUrl, pendingUploadFile);
      scenario.mark('blob_uploaded', {
        'Scenario.FileSizeBytes': pendingUploadFile.size,
      });
      const updated = await service.completeFileUpload(
        dataSetId,
        entry.dataId,
        {
          id: fileId,
          fileName: pendingUploadFile.name,
          fileType: uploadFileType as DataEntryFileType,
          fileSizeInBytes: pendingUploadFile.size,
        },
        usage,
      );
      scenario.mark('upload_completed');
      scenario.complete('success');
      setLocalFiles(updated.dataFiles ?? []);
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setFileError(
        err instanceof Error ? err.message : 'Failed to upload file',
      );
    } finally {
      setIsUploading(false);
    }
  }, [
    service,
    dataSetId,
    entry.dataId,
    pendingUploadFile,
    uploadFileType,
    scenarioFactory,
  ]);

  const handleSave = useCallback(async () => {
    await save(async () => {
      // Meeting entries carry their scalar fields as top-level request props,
      // sent only when non-empty (mirrors the Add Data Entry dialog).
      const meetingValues: Partial<Record<MeetingEntryFieldKey, string>> = {};
      if (isMeetingEntry) {
        for (const { key } of MEETING_ENTRY_FIELDS) {
          const value = editMeetingFields[key].trim();
          if (value !== '') meetingValues[key] = value;
        }
      }
      const request = {
        dataId: entry.dataId,
        name: editName.trim(),
        dataType: displayEntry.dataType,
        description: editDesc.trim() || undefined,
        // Preserve the existing free-form properties bag. The update is a PUT
        // that replaces the entry, so omitting this wipes the stored properties.
        properties: displayEntry.properties,
        ...meetingValues,
      };
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeUpdateDataEntry,
      );
      const usage = { extras: withCorrelationId(scenario.queryId) };
      try {
        try {
          await service.updateDataEntry(dataSetId, request, usage);
          onEntryUpdated();
          // The expansion panel renders from useDataEntryDetail's cache,
          // which the list refetch above does not touch. Invalidate it so
          // the just-saved meeting fields and properties show without a
          // full page reload.
          refetchDetail();
        } catch (err) {
          // Editing an entry on a published dataset returns 404 because no
          // draft version exists yet. Auto-demote to a fresh draft, then
          // retry the edit so the user is not blocked by a "Draft ... not
          // found" error. The retry stays inside the same traced scenario.
          if (err instanceof AppError && err.statusCode === 404) {
            await service.createDraftDataset(dataSetId, usage);
            scenario.mark('draft_created');
            await service.updateDataEntry(dataSetId, request, usage);
            // The parent switches to the draft version, which remounts the
            // table with fresh data, so no explicit refetch is needed here.
            onStatusChanged?.('Draft');
          } else {
            throw err;
          }
        }
        scenario.complete('success');
      } catch (error) {
        const classification = classifyScenarioError(error);
        scenario.complete(classification.status, classification.data);
        throw error;
      }
    });
  }, [
    save,
    service,
    scenarioFactory,
    dataSetId,
    entry.dataId,
    displayEntry.dataType,
    displayEntry.properties,
    isMeetingEntry,
    editMeetingFields,
    editName,
    editDesc,
    onEntryUpdated,
    onStatusChanged,
    refetchDetail,
  ]);

  if (isLoading) {
    return (
      <div className={styles.expansionLoading}>
        <Spinner size="small" label="Loading entry..." labelPosition="below" />
      </div>
    );
  }

  if (error !== null) {
    return <div className={styles.expansionError}>{error}</div>;
  }

  const files = localFiles ?? displayEntry.dataFiles ?? [];
  const previewFile = files.find(({ id }) => id === previewFileId) ?? null;

  const createdLabel = displayEntry.createdAt
    ? `Created ${new Date(displayEntry.createdAt).toLocaleString()}`
    : null;
  const updatedLabel = displayEntry.updatedAt
    ? `Updated ${new Date(displayEntry.updatedAt).toLocaleString()}`
    : null;

  const hasProperties =
    displayEntry.properties != null &&
    Object.keys(displayEntry.properties).length > 0;

  const meetingMetadata =
    displayEntry.dataType === MEETING_DATA_ENTRY_TYPE
      ? MEETING_ENTRY_FIELDS.map((field) => ({
          label: field.label,
          value: displayEntry[field.key],
        })).filter(
          (item): item is { label: string; value: string } =>
            typeof item.value === 'string' && item.value.trim() !== '',
        )
      : [];

  return (
    <div className={styles.expansionPanel}>
      {/* Meeting metadata */}
      {meetingMetadata.length > 0 && (
        <div className={styles.propertiesSection}>
          <span className={styles.propertiesLabel}>Meeting</span>
          <dl className={styles.metadataGrid}>
            {meetingMetadata.map((item) => (
              <div key={item.label} className={styles.metadataItem}>
                <dt className={styles.metadataItemLabel}>{item.label}</dt>
                <dd className={styles.metadataItemValue}>{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* Properties */}
      {hasProperties && (
        <div className={styles.propertiesSection}>
          <span className={styles.propertiesLabel}>Properties</span>
          <RawJsonViewer
            data={displayEntry.properties}
            filename={`${displayEntry.name ?? displayEntry.dataId}-properties.json`}
          />
        </div>
      )}

      {/* Dates + Edit */}
      {(createdLabel !== null || updatedLabel !== null) && (
        <div className={styles.dateRow}>
          <div className={styles.dateGroup}>
            {createdLabel !== null && <span>{createdLabel}</span>}
            {updatedLabel !== null && <span>{updatedLabel}</span>}
          </div>
          {canContribute ? (
            <Button
              appearance="transparent"
              size="small"
              icon={<EditIcon />}
              onClick={startEditing}
            >
              Edit
            </Button>
          ) : (
            <Tooltip
              content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
              relationship="description"
            >
              <Button
                appearance="transparent"
                size="small"
                icon={<EditIcon />}
                disabledFocusable
              >
                Edit
              </Button>
            </Tooltip>
          )}
        </div>
      )}

      {editError !== null && (
        <Body1 className={styles.dialogError}>{editError}</Body1>
      )}

      {editing && (
        <div className={styles.editForm}>
          <Field label="Name" required>
            <Input value={editName} onChange={(_, d) => setEditName(d.value)} />
          </Field>
          <Field label="Description">
            <Textarea
              value={editDesc}
              onChange={(_, d) => setEditDesc(d.value)}
              rows={EDIT_DESCRIPTION_ROWS}
            />
          </Field>
          {isMeetingEntry &&
            MEETING_ENTRY_FIELDS.map((field) => (
              <Field key={field.key} label={field.label}>
                <Input
                  type={field.isDate === true ? 'date' : 'text'}
                  placeholder={field.placeholder}
                  value={editMeetingFields[field.key]}
                  onChange={(_, d) => setEditMeetingField(field.key, d.value)}
                />
              </Field>
            ))}
          <div className={styles.editFormActions}>
            <Button
              appearance="primary"
              size="small"
              onClick={() => {
                void handleSave();
              }}
              disabled={!canSave}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
            <Button size="small" onClick={handleCancelEdit}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Files */}
      <div className={styles.filesSection}>
        <div className={styles.filesHeader}>
          <span className={styles.filesTitle}>Files ({files.length})</span>
          {canContribute ? (
            <Button
              appearance="primary"
              size="small"
              icon={
                isUploading ? <Spinner size="tiny" /> : <ArrowUpload16Regular />
              }
              disabled={isUploading}
              onClick={handleUploadSelect}
            >
              Upload File
            </Button>
          ) : (
            <Tooltip
              content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
              relationship="description"
            >
              <Button
                appearance="primary"
                size="small"
                icon={<ArrowUpload16Regular />}
                disabledFocusable
              >
                Upload File
              </Button>
            </Tooltip>
          )}
        </div>

        {fileError !== null && (
          <Body1 className={styles.dialogError}>{fileError}</Body1>
        )}

        {files.length > 0 && (
          <div className={styles.fileList}>
            {files.map((file) => (
              <Fragment key={file.id}>
                <div className={styles.fileItem}>
                  <div className={styles.fileInfo}>
                    <Button
                      appearance="transparent"
                      className={styles.fileName}
                      aria-expanded={previewFileId === file.id}
                      onClick={() =>
                        setPreviewFileId((current) =>
                          current === file.id ? null : file.id,
                        )
                      }
                    >
                      {file.fileName ?? file.id}
                    </Button>
                    <span className={styles.fileId}>{file.id}</span>
                  </div>
                  <div className={styles.fileActions}>
                    {onOpenViewer !== undefined &&
                      canLoadDataFilePreview(file) && (
                        <Tooltip content="Open in Viewer" relationship="label">
                          <Button
                            appearance="subtle"
                            size="small"
                            className={styles.actionButton}
                            icon={<MaximizeIcon />}
                            aria-label={`Open ${
                              file.fileName ?? file.id
                            } in Viewer`}
                            onClick={() => onOpenViewer(entry.dataId, file.id)}
                          />
                        </Tooltip>
                      )}
                    {canAnnotate && onAnnotateEntry !== undefined && (
                      <Tooltip content="Annotate" relationship="label">
                        <Button
                          appearance="subtle"
                          size="small"
                          className={styles.actionButton}
                          icon={<AnnotateIcon />}
                          aria-label={`Annotate ${file.fileName ?? file.id}`}
                          onClick={() =>
                            onAnnotateEntry(
                              entry.dataId,
                              dataSetVersion,
                              file.id,
                            )
                          }
                        />
                      </Tooltip>
                    )}
                    <Tooltip content="Download" relationship="label">
                      <Button
                        appearance="subtle"
                        size="small"
                        className={styles.actionButton}
                        icon={
                          downloadingFileId === file.id ? (
                            <Spinner size="tiny" />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                        disabled={downloadingFileId === file.id}
                        aria-label={`Download ${file.fileName ?? file.id}`}
                        onClick={() => {
                          void handleDownload(file);
                        }}
                      />
                    </Tooltip>
                    {canContribute ? (
                      <Tooltip content="Delete file" relationship="label">
                        <Button
                          appearance="subtle"
                          size="small"
                          className={styles.actionButton}
                          icon={
                            deletingFileId === file.id ? (
                              <Spinner size="tiny" />
                            ) : (
                              <DeleteIcon />
                            )
                          }
                          disabled={deletingFileId === file.id}
                          aria-label={`Delete ${file.fileName ?? file.id}`}
                          onClick={() => {
                            void handleDeleteFile(file.id);
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip
                        content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
                        relationship="description"
                      >
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<DeleteIcon />}
                          aria-label={`Delete ${file.fileName ?? file.id}`}
                          disabledFocusable
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
                {previewFile?.id === file.id && (
                  <DataFilePreview
                    file={previewFile}
                    loadSource={loadFileSource}
                    onClose={() => setPreviewFileId(null)}
                  />
                )}
              </Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Upload file dialog */}
      <Dialog
        open={pendingUploadFile !== null}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setPendingUploadFile(null);
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Upload File</DialogTitle>
            <DialogContent>
              <div>
                <Body1>File</Body1>
                <Input
                  value={pendingUploadFile?.name ?? ''}
                  readOnly
                  style={{ width: '100%', marginTop: '4px' }}
                />
              </div>
              <div style={{ marginTop: '12px' }}>
                <Body1>
                  File Type <span style={{ color: 'red' }}>*</span>
                </Body1>
                <Select
                  value={uploadFileType}
                  onChange={(_, data) => setUploadFileType(data.value)}
                  style={{ width: '100%', marginTop: '4px' }}
                  aria-label="File type"
                >
                  <option value={FILE_TYPE_PLACEHOLDER}>
                    Select file type...
                  </option>
                  {FILE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setPendingUploadFile(null)}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={uploadFileType === FILE_TYPE_PLACEHOLDER}
                onClick={() => {
                  void handleUploadConfirm();
                }}
              >
                Upload
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

// ── Entry row ─────────────────────────────────────────────────────────────────

interface EntryRowProps {
  entry: DataEntry;
  dataSetId: string;
  dataSetVersion: number;
  service: DataHubService;
  canContribute: boolean;
  canAnnotate: boolean;
  canOwn: boolean;
  onAnnotateEntry?: (
    dataId: string,
    dataSetVersion: number,
    fileId?: string,
  ) => void;
  onOpenViewer?: (dataId: string, fileId?: string) => void;
  onDeleteEntry: (entry: DataEntry) => void;
  onHardDeleteEntry: (entry: DataEntry) => void;
  onEntryUpdated: () => void;
  onStatusChanged?: (status: DatasetStatus) => void;
}

function EntryRow({
  entry,
  dataSetId,
  dataSetVersion,
  service,
  canContribute,
  canAnnotate,
  canOwn,
  onAnnotateEntry,
  onOpenViewer,
  onDeleteEntry,
  onHardDeleteEntry,
  onEntryUpdated,
  onStatusChanged,
}: EntryRowProps) {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(entry.dataId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_TIMEOUT_MS);
    });
  }, [entry.dataId]);

  const formattedDate = entry.createdAt
    ? new Date(entry.createdAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

  return (
    <>
      <tr
        className={mergeClasses(
          styles.tableRow,
          isExpanded ? styles.tableRowExpanded : undefined,
        )}
      >
        <td className={styles.tableCell}>
          <div className={styles.nameCell}>
            <Button
              appearance="subtle"
              size="small"
              className={styles.chevronButton}
              icon={
                isExpanded ? (
                  <ChevronDown16Regular />
                ) : (
                  <ChevronRight16Regular />
                )
              }
              onClick={() => setIsExpanded((v) => !v)}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse entry' : 'Expand entry'}
            />
            <span className={styles.entryName}>
              {entry.name ?? entry.dataId}
            </span>
          </div>
        </td>

        <td className={styles.tableCell}>
          <div className={styles.dataIdCell}>
            <span>{truncateId(entry.dataId)}</span>
            <Tooltip
              content={copied ? 'Copied!' : 'Copy data ID'}
              relationship="label"
            >
              <Button
                appearance="subtle"
                size="small"
                icon={<CopyIcon />}
                onClick={handleCopy}
                aria-label="Copy data ID"
              />
            </Tooltip>
          </div>
        </td>

        <td className={styles.tableCell}>{formattedDate}</td>

        <td className={styles.tableCell}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                size="small"
                icon={<MoreIcon />}
                aria-label={`Actions for ${entry.name ?? entry.dataId}`}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {canAnnotate && onAnnotateEntry !== undefined && (
                  <MenuItem
                    icon={<AnnotateIcon />}
                    onClick={() =>
                      onAnnotateEntry(entry.dataId, dataSetVersion)
                    }
                  >
                    Annotate
                  </MenuItem>
                )}
                {canContribute ? (
                  <MenuItem
                    icon={<DeleteIcon />}
                    onClick={() => onDeleteEntry(entry)}
                  >
                    Delete
                  </MenuItem>
                ) : (
                  <Tooltip
                    content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
                    relationship="description"
                  >
                    <MenuItem icon={<DeleteIcon />} disabled>
                      Delete
                    </MenuItem>
                  </Tooltip>
                )}
                {canOwn ? (
                  <MenuItem
                    icon={<HardDeleteIcon />}
                    className={styles.hardDeleteItem}
                    onClick={() => onHardDeleteEntry(entry)}
                  >
                    Hard Delete
                  </MenuItem>
                ) : (
                  <Tooltip
                    content={DATASET_ROLE_TOOLTIP_OWNER}
                    relationship="description"
                  >
                    <MenuItem
                      icon={<HardDeleteIcon />}
                      className={styles.hardDeleteItem}
                      disabled
                    >
                      Hard Delete
                    </MenuItem>
                  </Tooltip>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        </td>
      </tr>

      {isExpanded && (
        <tr className={styles.expansionRow}>
          <td className={styles.expansionCell} colSpan={4}>
            <EntryExpansionPanel
              entry={entry}
              dataSetId={dataSetId}
              dataSetVersion={dataSetVersion}
              service={service}
              canContribute={canContribute}
              canAnnotate={canAnnotate}
              onEntryUpdated={onEntryUpdated}
              onStatusChanged={onStatusChanged}
              onAnnotateEntry={onAnnotateEntry}
              onOpenViewer={onOpenViewer}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ── Tag search panel ──────────────────────────────────────────────────────────

const ATTR_PLACEHOLDER_VALUE = '';
const VALUE_PLACEHOLDER = '— value —';
const ATTR_PLACEHOLDER_LABEL = '— attribute —';

interface FilterRowProps {
  filter: TagFilter;
  index: number;
  attributeDefinitions: AttributeDefinition[];
  isLoadingAttributes: boolean;
  onFilterChange: (index: number, filter: TagFilter) => void;
  onFilterRemove: (index: number) => void;
}

function FilterRow({
  filter,
  index,
  attributeDefinitions,
  isLoadingAttributes,
  onFilterChange,
  onFilterRemove,
}: FilterRowProps) {
  const styles = useStyles();

  const selectedDef = attributeDefinitions.find(
    (d) => d.name === filter.attribute,
  );
  const hasEnumValues =
    selectedDef?.allowedValues != null && selectedDef.allowedValues.length > 0;

  const handleAttributeChange = useCallback(
    (_: React.ChangeEvent<HTMLSelectElement>, data: { value: string }) => {
      onFilterChange(index, { attribute: data.value, value: '' });
    },
    [index, onFilterChange],
  );

  const handleSelectValueChange = useCallback(
    (_: React.ChangeEvent<HTMLSelectElement>, data: { value: string }) => {
      onFilterChange(index, { ...filter, value: data.value });
    },
    [index, filter, onFilterChange],
  );

  const handleTextValueChange = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
      onFilterChange(index, { ...filter, value: data.value });
    },
    [index, filter, onFilterChange],
  );

  return (
    <div className={styles.filterRow}>
      <Select
        className={styles.filterInput}
        value={filter.attribute}
        onChange={handleAttributeChange}
        disabled={isLoadingAttributes}
        aria-label={`Filter attribute ${index + 1}`}
      >
        <option value={ATTR_PLACEHOLDER_VALUE}>{ATTR_PLACEHOLDER_LABEL}</option>
        {attributeDefinitions.map((def) => (
          <option key={def.attributeDefinitionId} value={def.name}>
            {def.displayName ?? def.name}
          </option>
        ))}
      </Select>

      {hasEnumValues ? (
        <Select
          className={styles.filterInput}
          value={filter.value}
          onChange={handleSelectValueChange}
          aria-label={`Filter value ${index + 1}`}
        >
          <option value="">{VALUE_PLACEHOLDER}</option>
          {selectedDef.allowedValues!.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </Select>
      ) : (
        <Input
          className={styles.filterInput}
          placeholder="value"
          value={filter.value}
          onChange={handleTextValueChange}
          disabled={!selectedDef}
          aria-label={`Filter value ${index + 1}`}
        />
      )}

      <Button
        appearance="subtle"
        size="small"
        icon={<Dismiss16Regular />}
        onClick={() => onFilterRemove(index)}
        aria-label="Remove filter"
      />
    </div>
  );
}

interface TagSearchPanelProps {
  filters: TagFilter[];
  matchMode: MatchMode;
  attributeDefinitions: AttributeDefinition[];
  isLoadingAttributes: boolean;
  onMatchModeChange: (mode: MatchMode) => void;
  onFilterChange: (index: number, filter: TagFilter) => void;
  onFilterRemove: (index: number) => void;
  onAddFilter: () => void;
  onSearch: () => void;
}

function TagSearchPanel({
  filters,
  matchMode,
  attributeDefinitions,
  isLoadingAttributes,
  onMatchModeChange,
  onFilterChange,
  onFilterRemove,
  onAddFilter,
  onSearch,
}: TagSearchPanelProps) {
  const styles = useStyles();

  return (
    <div className={styles.tagSearchPanel}>
      <div className={styles.matchModeRow}>
        <span className={styles.matchModeLabel}>Match mode:</span>
        <Button
          appearance={matchMode === MATCH_MODE_AND ? 'primary' : 'secondary'}
          size="small"
          onClick={() => onMatchModeChange(MATCH_MODE_AND)}
        >
          All (AND)
        </Button>
        <Button
          appearance={matchMode === MATCH_MODE_OR ? 'primary' : 'secondary'}
          size="small"
          onClick={() => onMatchModeChange(MATCH_MODE_OR)}
        >
          Any (OR)
        </Button>
      </div>

      {filters.map((filter, i) => (
        <FilterRow
          key={i}
          filter={filter}
          index={i}
          attributeDefinitions={attributeDefinitions}
          isLoadingAttributes={isLoadingAttributes}
          onFilterChange={onFilterChange}
          onFilterRemove={onFilterRemove}
        />
      ))}

      <div className={styles.filterActions}>
        <Button
          appearance="secondary"
          size="small"
          icon={<AddIcon />}
          onClick={onAddFilter}
        >
          Add Filter
        </Button>
        <Button appearance="secondary" size="small" onClick={onSearch}>
          Search
        </Button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function DatasetEntriesTable({
  dataSetId,
  dataSetVersion,
  datasetName,
  datasetStatus: _datasetStatus,
  dataType = 'Generic',
  readOnly = false,
  onStatusChanged,
  onAnnotateEntry,
  onOpenViewer,
  myRoles,
}: DatasetEntriesTableProps) {
  const styles = useStyles();
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);
  const scenarioFactory = useScenarioFactory();

  const demotedDuringSubmitRef = useRef(false);
  const [showTagSearch, setShowTagSearch] = useState(false);
  const [showParticipantFilter, setShowParticipantFilter] = useState(false);
  const [participantEntries, setParticipantEntries] = useState<
    DataEntry[] | null
  >(null);
  const [showAddEntryDialog, setShowAddEntryDialog] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [showUploadFolderDialog, setShowUploadFolderDialog] = useState(false);
  const [pendingDeleteEntry, setPendingDeleteEntry] =
    useState<DataEntry | null>(null);
  const [pendingHardDeleteEntry, setPendingHardDeleteEntry] =
    useState<DataEntry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [matchMode, setMatchMode] = useState<MatchMode>(MATCH_MODE_AND);
  const [tagFilters, setTagFilters] = useState<TagFilter[]>([
    { attribute: '', value: '' },
  ]);
  const [activeFilters, setActiveFilters] = useState<TagFilter[]>([]);
  const [activeMatchMode, setActiveMatchMode] =
    useState<MatchMode>(MATCH_MODE_AND);

  const { attributeDefinitions, isLoading: isLoadingAttributes } =
    useAttributeDefinitions();

  const apiFilters = useMemo((): DataEntryTagFilterInput[] => {
    const nonEmpty = activeFilters.filter(
      (f) => f.attribute.trim() !== '' && f.value.trim() !== '',
    );
    if (nonEmpty.length === 0) return [];

    return nonEmpty
      .map((f) => {
        const def = attributeDefinitions.find((d) => d.name === f.attribute);
        if (!def) return null;
        return {
          attributeDefinitionId: def.attributeDefinitionId,
          values: [f.value],
        };
      })
      .filter((f): f is DataEntryTagFilterInput => f !== null);
  }, [activeFilters, attributeDefinitions]);

  const hasActiveFilters = apiFilters.length > 0;

  const {
    entries,
    totalCount,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    refetch,
  } = useDatasetEntries({
    dataSetId,
    dataSetVersion,
    filters: hasActiveFilters ? apiFilters : undefined,
    matchMode: hasActiveFilters
      ? activeMatchMode === MATCH_MODE_AND
        ? TAG_SEARCH_MATCH_MODE_AND
        : TAG_SEARCH_MATCH_MODE_OR
      : undefined,
  });

  const handleSearch = useCallback(() => {
    setActiveFilters(tagFilters);
    setActiveMatchMode(matchMode);
  }, [tagFilters, matchMode]);

  const isMeetingDataset = dataType === MEETING_DATA_TYPE_LABEL;

  const toggleTagSearch = useCallback(() => {
    setShowTagSearch((prev) => !prev);
    setShowParticipantFilter(false);
    setParticipantEntries(null);
  }, []);

  const toggleParticipantFilter = useCallback(() => {
    setShowParticipantFilter((prev) => {
      const next = !prev;
      if (!next) setParticipantEntries(null);
      return next;
    });
    setShowTagSearch(false);
  }, []);

  const handleFilterChange = useCallback((index: number, filter: TagFilter) => {
    setTagFilters((prev) => {
      const next = [...prev];
      next[index] = filter;
      return next;
    });
  }, []);

  const handleFilterRemove = useCallback((index: number) => {
    setTagFilters((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length === 0 ? [{ attribute: '', value: '' }] : next;
    });
  }, []);

  const handleAddFilter = useCallback(() => {
    setTagFilters((prev) => [...prev, { attribute: '', value: '' }]);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (pendingDeleteEntry === null) return;
    setIsDeleting(true);
    setDeleteError(null);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeDeleteDataEntry,
    );
    const scenarioData = { 'Scenario.DeleteMode': 'soft' };
    try {
      await service.deleteDataEntry(dataSetId, pendingDeleteEntry.dataId, {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success', scenarioData);
      setPendingDeleteEntry(null);
      refetch();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete entry.',
      );
    } finally {
      setIsDeleting(false);
    }
  }, [service, scenarioFactory, dataSetId, pendingDeleteEntry, refetch]);

  const handleHardDeleteConfirm = useCallback(async () => {
    if (pendingHardDeleteEntry === null) return;
    setIsDeleting(true);
    setDeleteError(null);
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeHardDeleteDataEntry,
    );
    const scenarioData = { 'Scenario.DeleteMode': 'hard' };
    try {
      await service.hardDeleteDataEntry(
        dataSetId,
        pendingHardDeleteEntry.dataId,
        {
          extras: withCorrelationId(scenario.queryId),
        },
      );
      scenario.complete('success', scenarioData);
      setPendingHardDeleteEntry(null);
      refetch();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, {
        ...scenarioData,
        ...classification.data,
      });
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to hard delete entry.',
      );
    } finally {
      setIsDeleting(false);
    }
  }, [service, scenarioFactory, dataSetId, pendingHardDeleteEntry, refetch]);

  const isParticipantMode = participantEntries !== null;
  const displayEntries = participantEntries ?? entries;

  return (
    <div className={styles.root}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarToggles}>
          {(entries.length > 0 ||
            showTagSearch ||
            participantEntries !== null) && (
            <Tooltip
              content={showTagSearch ? 'Hide filters' : 'Show filters'}
              relationship="label"
            >
              <Button
                appearance={showTagSearch ? 'primary' : 'secondary'}
                size="small"
                icon={<FilterIcon />}
                onClick={toggleTagSearch}
                aria-pressed={showTagSearch}
                aria-label="Toggle filters"
              />
            </Tooltip>
          )}
          {isMeetingDataset &&
            (entries.length > 0 ||
              showParticipantFilter ||
              participantEntries !== null) && (
              <Button
                appearance={showParticipantFilter ? 'primary' : 'secondary'}
                size="small"
                icon={<PeopleIcon />}
                onClick={toggleParticipantFilter}
                aria-pressed={showParticipantFilter}
                aria-label={PARTICIPANT_FILTER_TOGGLE_ARIA}
              >
                {showParticipantFilter
                  ? PARTICIPANT_FILTER_HIDE_LABEL
                  : PARTICIPANT_FILTER_SHOW_LABEL}
              </Button>
            )}
        </div>
        <div className={styles.toolbarActions}>
          {!readOnly &&
            (hasDatasetRole(myRoles, DATASET_ROLE_CONTRIBUTOR) ? (
              <Button
                appearance="subtle"
                size="small"
                icon={<UploadIcon />}
                onClick={() => setShowUploadFolderDialog(true)}
              >
                Upload Folder
              </Button>
            ) : (
              <Tooltip
                content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
                relationship="description"
              >
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<UploadIcon />}
                  disabledFocusable
                >
                  Upload Folder
                </Button>
              </Tooltip>
            ))}
          <Button
            appearance="subtle"
            size="small"
            icon={<DownloadIcon />}
            onClick={() => setShowDownloadDialog(true)}
          >
            Download Dataset
          </Button>
          {!readOnly &&
            (hasDatasetRole(myRoles, DATASET_ROLE_CONTRIBUTOR) ? (
              <Button
                appearance="primary"
                size="small"
                icon={<AddIcon />}
                aria-label="Add Entry"
                onClick={() => setShowAddEntryDialog(true)}
              >
                Add Entry
              </Button>
            ) : (
              <Tooltip
                content={DATASET_ROLE_TOOLTIP_CONTRIBUTOR}
                relationship="description"
              >
                <Button
                  appearance="primary"
                  size="small"
                  icon={<AddIcon />}
                  aria-label="Add Entry"
                  disabledFocusable
                >
                  Add Entry
                </Button>
              </Tooltip>
            ))}
        </div>
      </div>

      {/* Tag search panel */}
      {showTagSearch && (
        <TagSearchPanel
          filters={tagFilters}
          matchMode={matchMode}
          attributeDefinitions={attributeDefinitions}
          isLoadingAttributes={isLoadingAttributes}
          onMatchModeChange={setMatchMode}
          onFilterChange={handleFilterChange}
          onFilterRemove={handleFilterRemove}
          onAddFilter={handleAddFilter}
          onSearch={handleSearch}
        />
      )}

      {/* Participant filter panel (meeting datasets only) */}
      {isMeetingDataset && showParticipantFilter && (
        <ParticipantSearchPanel
          dataSetId={dataSetId}
          datasetName={datasetName}
          service={service}
          canHardDelete={hasDatasetRole(myRoles, DATASET_ROLE_OWNER)}
          onResultsChange={setParticipantEntries}
          onParticipantDataPurged={refetch}
        />
      )}

      {/* Table */}
      {isLoading && !isParticipantMode ? (
        <div className={styles.loadingState}>
          <Spinner label="Loading entries..." />
        </div>
      ) : error !== null && !isParticipantMode ? (
        <div className={styles.errorState}>
          <Body1>Failed to load entries: {error}</Body1>
        </div>
      ) : displayEntries.length === 0 ? (
        <div className={styles.emptyState}>
          <Body1>
            {isParticipantMode
              ? PARTICIPANT_SEARCH_EMPTY_MESSAGE
              : 'No entries found.'}
          </Body1>
        </div>
      ) : (
        <table className={styles.table} aria-label="Dataset entries">
          <thead>
            <tr className={styles.tableHeader}>
              <th className={styles.tableHeaderCell}>Name</th>
              <th className={styles.tableHeaderCell}>Data ID</th>
              <th className={styles.tableHeaderCell}>Created</th>
              <th className={styles.tableHeaderCell} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {displayEntries.map((entry) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                dataSetId={dataSetId}
                dataSetVersion={dataSetVersion}
                service={service}
                canContribute={hasDatasetRole(
                  myRoles,
                  DATASET_ROLE_CONTRIBUTOR,
                )}
                canAnnotate={hasDatasetRole(myRoles, DATASET_ROLE_ANNOTATOR)}
                canOwn={hasDatasetRole(myRoles, DATASET_ROLE_OWNER)}
                onAnnotateEntry={onAnnotateEntry}
                onOpenViewer={onOpenViewer}
                onDeleteEntry={setPendingDeleteEntry}
                onHardDeleteEntry={setPendingHardDeleteEntry}
                onEntryUpdated={refetch}
                onStatusChanged={onStatusChanged}
              />
            ))}
          </tbody>
        </table>
      )}

      {/* Load more footer (base list only; participant results paginate in-panel) */}
      {!isParticipantMode &&
        !isLoading &&
        error === null &&
        entries.length > 0 && (
          <div className={styles.footer}>
            <span className={styles.footerLeft}>
              {totalCount !== null
                ? `Showing ${entries.length} of ${totalCount} entries`
                : `${entries.length} entries`}
            </span>

            {hasMore && (
              <Button
                appearance="subtle"
                disabled={isLoadingMore}
                onClick={loadMore}
              >
                {isLoadingMore ? 'Loading...' : 'Load more'}
              </Button>
            )}
          </div>
        )}

      <AddDataEntryDialog
        open={showAddEntryDialog}
        onClose={() => setShowAddEntryDialog(false)}
        onSubmit={async (request) => {
          const scenario = scenarioFactory.newScenario(
            ScenarioName.DatalakeCreateDataEntry,
          );
          const usage = { extras: withCorrelationId(scenario.queryId) };
          demotedDuringSubmitRef.current = false;
          try {
            let createdEntry: DataEntry;
            try {
              createdEntry = await service.createDataEntry(
                dataSetId,
                request,
                usage,
              );
            } catch (err) {
              if (!(err instanceof AppError) || err.statusCode !== 404) {
                throw err;
              }
              await service.createDraftDataset(dataSetId, usage);
              scenario.mark('draft_created');
              demotedDuringSubmitRef.current = true;
              createdEntry = await service.createDataEntry(
                dataSetId,
                request,
                usage,
              );
            }
            scenario.complete('success');
            return createdEntry;
          } catch (error) {
            const classification = classifyScenarioError(error);
            scenario.complete(classification.status, classification.data);
            throw error;
          }
        }}
        onCreated={() => {
          if (demotedDuringSubmitRef.current) {
            demotedDuringSubmitRef.current = false;
            onStatusChanged?.('Draft');
          } else {
            refetch();
          }
        }}
      />

      <Dialog
        open={pendingDeleteEntry !== null}
        onOpenChange={(_e, data) => {
          if (!data.open && !isDeleting) {
            setPendingDeleteEntry(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete entry?</DialogTitle>
            <DialogContent>
              <Body1>
                &ldquo;{pendingDeleteEntry?.name ?? pendingDeleteEntry?.dataId}
                &rdquo; will be deleted.
              </Body1>
              {deleteError !== null && (
                <Body1 className={styles.dialogError}>{deleteError}</Body1>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                icon={isDeleting ? <Spinner size="tiny" /> : undefined}
                disabled={isDeleting}
                onClick={() => {
                  void handleDeleteConfirm();
                }}
              >
                Delete
              </Button>
              <Button
                appearance="secondary"
                disabled={isDeleting}
                onClick={() => {
                  setPendingDeleteEntry(null);
                  setDeleteError(null);
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog
        open={pendingHardDeleteEntry !== null}
        onOpenChange={(_e, data) => {
          if (!data.open && !isDeleting) {
            setPendingHardDeleteEntry(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Hard delete entry?</DialogTitle>
            <DialogContent>
              <Body1>
                &ldquo;
                {pendingHardDeleteEntry?.name ?? pendingHardDeleteEntry?.dataId}
                &rdquo; will be permanently deleted, bypassing soft-delete
                recovery. This action cannot be undone.
              </Body1>
              {deleteError !== null && (
                <Body1 className={styles.dialogError}>{deleteError}</Body1>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                data-testid="entry-hard-delete-confirm-button"
                appearance="primary"
                className={styles.hardDeleteConfirmButton}
                icon={isDeleting ? <Spinner size="tiny" /> : undefined}
                disabled={isDeleting}
                onClick={() => {
                  void handleHardDeleteConfirm();
                }}
              >
                Hard Delete
              </Button>
              <Button
                appearance="secondary"
                disabled={isDeleting}
                onClick={() => {
                  setPendingHardDeleteEntry(null);
                  setDeleteError(null);
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <DownloadDatasetDialog
        open={showDownloadDialog}
        dataSetId={dataSetId}
        currentVersion={dataSetVersion}
        datasetName={datasetName}
        onClose={() => setShowDownloadDialog(false)}
      />

      <UploadFolderDialog
        open={showUploadFolderDialog}
        dataSetId={dataSetId}
        defaultDataType={dataType}
        onClose={() => setShowUploadFolderDialog(false)}
        onComplete={() => refetch()}
        onStatusChanged={() => onStatusChanged?.('Draft')}
      />
    </div>
  );
}
